/**
 * The menu for one or more songs: under every row's ⋯, on right-click, and in
 * the selection bar. Core actions first, then whatever plugins added.
 */

import type { AppState } from "../core/app";
import {
  addToPlaylist,
  archive,
  newPlaylist,
  playSimilar,
  changeCover,
  reanalyse,
  removeCover,
  removeFromLibrary,
  showInFolder,
} from "../core/commands";
import { player } from "../core/player";
import { editableColumns, editableFor, type EditableColumn } from "../core/edits";
import { registry, type MenuItem } from "../core/registry";
import { openFlag, openRelease, openSetValue } from "../shell/EditDialogs";
import { setSongsAvailability } from "../api";
import { markFixed, reasonLabel } from "../core/needsEdit";
import { MOD } from "../shell/menus";
import { toast } from "../shell/Toast";
import { startBpmComparison } from "../shell/DeepRhythm";
import type { Track } from "../types";

/**
 * A song's (or a selection's) menu. `columnId` is the column that was
 * right-clicked: its value can be edited straight from here, for one song or
 * all the selected ones together.
 */
export function trackMenu(app: AppState, tracks: Track[], context: Track[] = tracks, columnId?: string): MenuItem[] {
  if (!tracks.length) return [];
  const one = tracks.length === 1 ? tracks[0] : null;
  const what = one ? "" : ` (${tracks.length})`;
  const offline = app.offline;
  const ids = tracks.map((t) => t.id);
  const clicked = editableFor(columnId, app.library.columns);
  const editColumn = (col: EditableColumn): MenuItem => ({
    id: `col-${col.id}`, label: one ? `${col.label}…` : `${col.label} for ${tracks.length} songs…`,
    disabled: offline, run: () => openSetValue(ids, col),
  });
  const items: MenuItem[] = [
    ...(clicked
      ? [{
          id: "edit-clicked", group: "0", label: one ? `Edit ${clicked.label}…` : `Set ${clicked.label} for ${tracks.length} songs…`,
          icon: "edit" as const, disabled: offline, permission: "library.edit", run: () => openSetValue(ids, clicked),
        }]
      : []),
    {
      id: "play", group: "1", label: one ? "Play" : `Play ${tracks.length} songs`, icon: "play", shortcut: "Enter",
      run: () => {
        // One song plays in the context of the list it was picked from.
        if (one && context.length > 1) player.playTracks(context, Math.max(0, context.findIndex((t) => t.id === one.id)));
        else player.playTracks(tracks);
      },
    },
    { id: "next", group: "1", label: `Play next${what}`, icon: "next", shortcut: `${MOD}+Enter`, run: () => { player.playNext(tracks); toast(one ? `“${one.title}” plays next.` : `${tracks.length} songs play next.`, "ok"); } },
    { id: "queue", group: "1", label: `Add to queue${what}`, icon: "queue", shortcut: "Q", run: () => { player.enqueue(tracks); toast(one ? `Added “${one.title}” to the queue.` : `Added ${tracks.length} songs to the queue.`, "ok"); } },
    {
      id: "playlist", group: "1", label: "Add to playlist", icon: "list", disabled: offline, permission: "playlists.edit",
      submenu: [
        { id: "new", group: "a", label: "New playlist…", icon: "plus", run: () => newPlaylist(app, tracks) },
        ...app.library.playlists.map((pl) => ({ id: `pl${pl.id}`, group: "b", label: pl.name, run: () => addToPlaylist(app, pl.id, tracks, pl.name) })),
      ],
    },
    { id: "similar", group: "2", label: "Play similar songs", icon: "spark", disabled: !one || offline, hint: "Songs with the closest energy, tempo and danceability", run: () => { if (one) void playSimilar(one); } },
    {
      id: "flow", group: "2", label: "Start a Flow from here", icon: "wave", disabled: !one, permission: "flows.use",
      hint: "Opens a new flow that begins with this song",
      run: () => { if (one) app.navigate("flow", { new: "1", seed: one.id, seedLabel: `${one.title} — ${one.artist}` }); },
    },
    { id: "edit", group: "3", permission: "library.edit", label: one ? "Edit tags…" : `Edit tags of ${tracks.length} songs…`, icon: "edit", shortcut: `${MOD}+I`, run: () => app.navigate("studio", { ids: ids.join(",") }) },
    {
      id: "edit-column", group: "3", label: "Edit a column", icon: "columns", disabled: offline, permission: "library.edit",
      submenu: editableColumns(app.library.columns).map(editColumn),
    },
    {
      id: "analyse", group: "3", label: "Re-analyse", icon: "refresh", disabled: offline, permission: "library.edit", hint: "Measure again; your own edits are kept",
      submenu: [
        { id: "main", label: "BPM, key and loudness", run: () => reanalyse(app, tracks) },
        { id: "all", label: "Also energy and danceability", hint: "Only needed after Nova's energy method changes: unchanged audio rates the same", run: () => reanalyse(app, tracks, true) },
        { id: "compare-bpm", label: "Compare BPM: Nova and DeepRhythm", hint: "Measures both ways for Settings → BPM comparison; changes nothing", run: () => void startBpmComparison(app, tracks) },
      ],
    },
    {
      id: "cover", group: "3", label: "Cover art", icon: "disc", disabled: !one || offline, permission: "library.edit", hint: one ? undefined : "One song at a time",
      submenu: [
        { id: "change", label: "Change cover…", run: () => { if (one) void changeCover(one); } },
        { id: "remove", label: "Remove cover", danger: true, run: () => { if (one) void removeCover(one); } },
      ],
    },
    ...(() => {
      const flagged = tracks.filter((t) => t.needsEdit);
      const items: MenuItem[] = [];
      if (flagged.length < tracks.length) {
        items.push({ id: "flag", group: "3", permission: "library.flag", label: one ? "Flag for editing…" : `Flag ${tracks.length - flagged.length} for editing…`, icon: "bell", disabled: offline, hint: "Tell whoever edits songs that this one needs work", run: () => openFlag(tracks.filter((t) => !t.needsEdit).map((t) => t.id)) });
      }
      if (flagged.length) {
        items.push({ id: "fixed", group: "3", permission: "library.edit", label: one ? "Mark fixed" : `Mark ${flagged.length} fixed`, icon: "check", disabled: offline, hint: one ? `Flagged: ${reasonLabel(flagged[0].needsEdit!.reason)}` : undefined, run: () => void markFixed(flagged.map((t) => t.id), one?.title) });
      }
      return items;
    })(),
    ...(() => {
      // Hand-picked only: Auto DJ and Flow never pick it; search and add it yourself.
      const own = tracks.filter((t) => t.ownManualOnly);
      const byFolder = tracks.filter((t) => t.manualFolder && !t.ownManualOnly);
      const turnOn = own.length < tracks.length;
      const items: MenuItem[] = [{
        id: "manual", group: "3", permission: "library.edit",
        label: turnOn ? (one ? "Hand-picked only" : `Hand-picked only (${tracks.length - own.length})`) : (one ? "✓ Hand-picked only" : `Not hand-picked only (${own.length})`),
        icon: "user", disabled: offline,
        hint: one?.manualFolder && !one.ownManualOnly
          ? `Already hand-picked only: its folder “${one.manualFolder}” is`
          : "Auto DJ and Flow never pick it; search and add it yourself. Clip rules still use a clip.",
        run: async () => {
          const out = await setSongsAvailability(ids, { manualOnly: turnOn });
          if (out.error) return toast(out.error, "bad");
          toast(turnOn
            ? `${one ? `“${one.title}” is` : `${tracks.length} songs are`} hand-picked only.`
            : `${one ? `“${one.title}”` : `${own.length} songs`} can be picked by Auto DJ and Flow again${byFolder.length ? " (its folder still is not)" : ""}.`, "ok");
        },
      }, {
        id: "release", group: "3", permission: "library.edit",
        label: one?.availableFrom ? `Release date: ${one.availableFrom.replace("T", " ")}…` : "Release date…",
        icon: "clock", disabled: offline,
        hint: "Nothing plays it by itself before this date and time",
        run: () => openRelease(ids, one?.availableFrom ?? tracks.find((t) => t.availableFrom)?.availableFrom ?? null),
      }];
      return items;
    })(),
    { id: "folder", group: "3", label: "Show in folder", icon: "folder", disabled: !one, run: () => { if (one) void showInFolder(one); } },
    { id: "info", group: "3", label: "Properties", icon: "info", disabled: !one, run: () => { if (one) { app.setFocused(one.id); app.setInspector(true); } } },
    { id: "archive", group: "4", permission: "library.delete", label: `Archive${what}`, icon: "archive", disabled: offline, hint: "Hide from the library without deleting anything", run: () => archive(app, tracks) },
    { id: "remove", group: "4", permission: "library.delete", label: `Remove from library${what}`, icon: "trash", shortcut: "Del", danger: true, disabled: offline, run: () => removeFromLibrary(app, tracks) },
  ];
  const extra = registry.get().trackActions.filter((a) => !a.when || a.when(tracks));
  for (const a of extra) {
    items.push({ id: `x-${a.id}`, group: `9-${a.group}`, label: a.label, icon: a.icon, shortcut: a.shortcut, danger: a.danger, permission: a.permission, run: () => a.run(tracks) });
  }
  return items;
}
